import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AvailabilityFullCalendar from './AvailabilityFullCalendar';
import { availabilityAPI } from '../services/api';
import styles from '../styles/UserDashboard.module.css';
import {
  displayDateDDMMMYYYY,
  getTodayKualaLumpurISODate,
  toKualaLumpurISODateStrict,
} from '../utils/dateFormat';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';

const AvailabilityCalendar = ({ userId, isAdmin = false }) => {
  const { user } = useAuth();
  const { t } = useLocalization();

  const [dates, setDates] = useState([]); // Saved dates from backend
  const [pendingDates, setPendingDates] = useState([]); // Staged dates (not saved yet)
  const [editing, setEditing] = useState(false);
  const [userAvailability, setUserAvailability] = useState([]); // For admin: user availability on selected day
  const [selectedDate, setSelectedDate] = useState(null); // For admin
  const [customEvents, setCustomEvents] = useState([]);
  const [customTitle, setCustomTitle] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [submittingCustom, setSubmittingCustom] = useState(false);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [activeCustomEvent, setActiveCustomEvent] = useState(null);
  const [customEditTitle, setCustomEditTitle] = useState('');
  const [customEditDescription, setCustomEditDescription] = useState('');
  const [customEditDate, setCustomEditDate] = useState('');
  const [editingCustomEvent, setEditingCustomEvent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const todayISO = getTodayKualaLumpurISODate();
  const canManageCustomEvents = !!user && (user.role === 'admin' || user.role === 'supervisor');

  const getThreeMonthsLaterISO = (baseISO) => {
    const [year, month, day] = String(baseISO).split('-').map(Number);
    if (!year || !month || !day) return baseISO;
    const dt = new Date(year, month - 1, day);
    dt.setMonth(dt.getMonth() + 3);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const fetchPlanned = useCallback(async (uid) => {
    setLoading(true);
    setError('');
    try {
      if (uid) {
        const res = await availabilityAPI.getPlanned(uid);
        // Treat clocked-in users as available even if available flag is stale.
        let planned = (res.data || [])
          .filter((d) => Number(d.available) === 1 || !!d.login_time)
          .map((d) => toKualaLumpurISODateStrict(d.availability_date));
        // Deduplicate and sort
        planned = Array.from(new Set(planned)).sort();
        setDates(planned);
        setPendingDates(planned);
        setEditing(false);
      }
    } catch (e) {
      setError(t('availability_planned_load_failed', 'Failed to load planned availability'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchCustomEvents = useCallback(async () => {
    try {
      const res = await availabilityAPI.getCustomEvents();
      setCustomEvents(res.data || []);
    } catch (e) {
      setError(t('availability_custom_events_load_failed', 'Failed to load custom events'));
    }
  }, [t]);

  useEffect(() => {
    if (!isAdmin) {
      (async () => {
        await fetchPlanned(userId);
        await fetchCustomEvents();
      })();
    } else {
      (async () => {
        setLoading(true);
        try {
          await fetchCustomEvents();
        } catch (e) {
          setError(t('availability_custom_events_load_failed', 'Failed to load custom events'));
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [userId, isAdmin, fetchPlanned, fetchCustomEvents, t]);

  const upcomingAdminEvents = useMemo(() => {
    const endISO = getThreeMonthsLaterISO(todayISO);
    return (customEvents || [])
      .filter((evt) => String(evt.created_by_role || '').toLowerCase() === 'admin')
      .map((evt) => ({
        ...evt,
        eventDateISO: toKualaLumpurISODateStrict(evt.event_date),
      }))
      .filter((evt) => evt.eventDateISO && evt.eventDateISO >= todayISO && evt.eventDateISO <= endISO)
      .sort((a, b) => a.eventDateISO.localeCompare(b.eventDateISO));
  }, [customEvents, todayISO]);

  const visibleUpcomingEvents = useMemo(() => {
    if (showAllUpcoming) return upcomingAdminEvents;
    return upcomingAdminEvents.slice(0, 5);
  }, [showAllUpcoming, upcomingAdminEvents]);

  const hasPendingChanges = useMemo(() => {
    if (dates.length !== pendingDates.length) return true;
    for (let i = 0; i < dates.length; i += 1) {
      if (dates[i] !== pendingDates[i]) return true;
    }
    return false;
  }, [dates, pendingDates]);

  // Add or remove a date in staged mode (save/cancel controls persistence).
  const handleUserDateClick = (date) => {
    const isoDay = toKualaLumpurISODateStrict(date);
    if (!isoDay || isoDay < todayISO) return;

    setPendingDates((prev) => {
      let newDates;
      if (prev.includes(isoDay)) {
        newDates = prev.filter((d) => d !== isoDay);
      } else {
        newDates = [...prev, isoDay];
      }
      return [...newDates].sort();
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (isAdmin) return;
    setLoading(true);
    setError('');
    try {
      await availabilityAPI.setAvailability({ dates: pendingDates });
      await fetchPlanned(userId);
      setEditing(false);
    } catch (e) {
      setError(t('availability_update_failed', 'Failed to update availability'));
      console.error('Error saving availability:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPendingDates(dates);
    setEditing(false);
  };

  // For admin/supervisor: fetch all users' availability for selected day.
  const handleAdminDateClick = async (date) => {
    const isoDay = toKualaLumpurISODateStrict(date);
    setSelectedDate(isoDay);
    setLoading(true);
    setError('');
    try {
      const res = await availabilityAPI.getAvailabilityByDate(isoDay);
      setUserAvailability(res.data || []);
    } catch (e) {
      setError(t('availability_user_load_failed', 'Failed to load user availability'));
    } finally {
      setLoading(false);
    }
  };

  const handleCustomEventCreate = async () => {
    if (!selectedDate || !customTitle.trim()) {
      setError(t('availability_custom_event_validation', 'Select a date and enter event title'));
      return;
    }
    setSubmittingCustom(true);
    setError('');
    try {
      await availabilityAPI.createCustomEvent({
        date: selectedDate,
        title: customTitle.trim(),
        description: customDescription.trim(),
      });
      setCustomTitle('');
      setCustomDescription('');
      await fetchCustomEvents();
    } catch (e) {
      setError(e?.response?.data?.message || t('availability_custom_event_create_failed', 'Failed to create custom event'));
    } finally {
      setSubmittingCustom(false);
    }
  };

  const openCustomEventDialog = (eventPayload) => {
    setActiveCustomEvent(eventPayload);
    setCustomEditTitle(eventPayload?.title || '');
    setCustomEditDescription(eventPayload?.description || '');
    setCustomEditDate(toKualaLumpurISODateStrict(eventPayload?.event_date));
    setEditingCustomEvent(false);
  };

  const closeCustomEventDialog = () => {
    setActiveCustomEvent(null);
    setEditingCustomEvent(false);
  };

  const handleCustomEventUpdate = async () => {
    if (!activeCustomEvent) return;
    if (!customEditTitle.trim()) {
      setError(t('availability_custom_event_title_required', 'Custom event title is required'));
      return;
    }
    setSubmittingCustom(true);
    setError('');
    try {
      await availabilityAPI.updateCustomEvent(activeCustomEvent.id, {
        date: customEditDate || undefined,
        title: customEditTitle.trim(),
        description: customEditDescription.trim(),
      });
      await fetchCustomEvents();
      closeCustomEventDialog();
    } catch (e) {
      setError(e?.response?.data?.message || t('availability_custom_event_update_failed', 'Failed to update custom event'));
    } finally {
      setSubmittingCustom(false);
    }
  };

  const handleCustomEventDelete = async () => {
    if (!activeCustomEvent) return;
    const confirmed = window.confirm(`${t('availability_custom_event_delete_confirm_prefix', 'Delete custom event')} "${activeCustomEvent.title}"?`);
    if (!confirmed) return;
    setSubmittingCustom(true);
    setError('');
    try {
      await availabilityAPI.deleteCustomEvent(activeCustomEvent.id);
      await fetchCustomEvents();
      closeCustomEventDialog();
    } catch (e) {
      setError(e?.response?.data?.message || t('availability_custom_event_delete_failed', 'Failed to delete custom event'));
    } finally {
      setSubmittingCustom(false);
    }
  };

  const calendarEvents = useMemo(() => {
    if (isAdmin) {
      return customEvents.map((evt) => ({
        id: `custom-${evt.id}`,
        title: evt.title,
        date: toKualaLumpurISODateStrict(evt.event_date),
        classNames: [styles.customCalendarEvent],
        extendedProps: {
          kind: 'custom',
          payload: {
            id: evt.id,
            event_date: evt.event_date,
            title: evt.title,
            description: evt.description,
            created_by_username: evt.created_by_username,
          },
        },
      }));
    }

    const savedSet = new Set(dates);
    const pendingSet = new Set(pendingDates);
    const events = [];

    pendingDates.forEach((date) => {
      events.push({
        id: `pending-${date}`,
        title: '',
        date,
        classNames: [styles.userAvailabilityDot, savedSet.has(date) ? styles.availableCalendarEvent : styles.addedCalendarEvent],
        extendedProps: { kind: 'availability' },
      });
    });

    dates.forEach((date) => {
      if (!pendingSet.has(date)) {
        events.push({
          id: `removed-${date}`,
          title: '',
          date,
          classNames: [styles.userAvailabilityDot, styles.removedCalendarEvent],
          extendedProps: { kind: 'availability' },
        });
      }
    });

    return events;
  }, [isAdmin, dates, pendingDates, customEvents]);

  return (
    <>
      <div className={isAdmin ? styles.availabilityCalendarAdminWrap : styles.availabilityCalendarWrap}>
      <h3 className={styles.calendarTitle}>{isAdmin ? (user?.role === 'supervisor' ? t('availability_team_members_title', "Team Members' Availability") : t('nav_all_users_availability', "All Users' Availability")) : t('availability_plan_days', 'Plan Your Available Days')}</h3>
      <AvailabilityFullCalendar
        events={calendarEvents}
        onDateClick={(info) => (isAdmin ? handleAdminDateClick(info.date) : handleUserDateClick(info.date))}
        onEventClick={(info) => {
          if (isAdmin) {
            const isCustom = info.event.extendedProps?.kind === 'custom';
            if (isCustom) {
              openCustomEventDialog(info.event.extendedProps?.payload || null);
            } else {
              handleAdminDateClick(info.event.start);
            }
          } else {
            handleUserDateClick(info.event.start);
          }
        }}
        validRange={!isAdmin ? { start: todayISO } : undefined}
        dayCellClassNames={
          !isAdmin
            ? (arg) => {
                const iso = toKualaLumpurISODateStrict(arg.date);
                return iso < todayISO ? [styles.pastDayCell] : [];
              }
            : undefined
        }
      />

      {!isAdmin && editing && (
        <div className={styles.saveCancelWrapCentered}>
          <button
            type="button"
            className={styles.saveBtn}
            disabled={loading || !hasPendingChanges}
            onClick={handleSave}
          >
            {loading ? t('common_saving', 'Saving...') : t('availability_save', 'Save Availability')}
          </button>
          <button
            type="button"
            className={styles.cancelBtn}
            disabled={loading || !hasPendingChanges}
            onClick={handleCancel}
          >
            {t('common_cancel', 'Cancel')}
          </button>
        </div>
      )}

      {error && <p className={styles.error}>{error}</p>}
      {loading && <p className={styles.loading}>{t('clock_loading', 'Loading...')}</p>}

      {isAdmin && selectedDate && !loading && (
        <div className={styles.adminUserListWrap}>
          <h4 className={styles.adminUserListTitle}>{t('availability_for_date', 'Availability for')} {selectedDate ? displayDateDDMMMYYYY(selectedDate) : ''}</h4>

          {canManageCustomEvents && (
            <div className={styles.customEventForm}>
              <input
                type="text"
                placeholder={t('availability_custom_event_title_placeholder', 'Custom event title')}
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                className={styles.customEventInput}
              />
              <input
                type="text"
                placeholder={t('availability_custom_event_description_placeholder', 'Optional description')}
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                className={styles.customEventInput}
              />
              <button
                type="button"
                className={styles.saveBtn}
                disabled={submittingCustom || !customTitle.trim()}
                onClick={handleCustomEventCreate}
              >
                {submittingCustom ? t('availability_custom_event_adding', 'Adding...') : t('availability_add_custom_event', 'Add Custom Event')}
              </button>
            </div>
          )}

          <div className={styles.adminUserCardGrid}>
            {userAvailability.map((item) => (
              <div
                key={item.user_id}
                className={`${styles.adminUserCard} ${Number(item.is_available) === 1 ? styles.adminUserCardAvailable : styles.adminUserCardUnavailable}`}
              >
                <div className={styles.adminUserCardHeader}>
                  <span className={styles.adminUserName}>{item.username || item.email}</span>
                  <span className={styles.adminUserRole}>{item.role}</span>
                  <span
                    className={`${styles.adminAvailabilityDot} ${Number(item.is_available) === 1 ? styles.adminAvailabilityDotOn : styles.adminAvailabilityDotOff}`}
                  ></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isAdmin && editing && (
        <>
          <div className={styles.calendarLegend}>
            <div className={styles.legendItem}>
              <span className={`${styles.legendCircle} ${styles.availableDay}`}></span>
              <span className={styles.legendLabel}>{t('availability_available', 'Available')}</span>
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendCircle} ${styles.addedCalendarEventMarker}`}></span>
              <span className={styles.legendLabel}>{t('availability_will_add', 'Will Add')}</span>
            </div>
            <div className={styles.legendItem}>
              <span className={`${styles.legendCircle} ${styles.removedCalendarEventMarker}`}></span>
              <span className={styles.legendLabel}>{t('availability_will_remove', 'Will Remove')}</span>
            </div>
          </div>

          <p className={styles.calendarHint}>{t('availability_calendar_hint', 'Click a date to stage add/remove changes, then Save. Past dates are not editable.')}</p>
        </>
      )}

      {isAdmin && activeCustomEvent && (
        <div className={styles.customEventModalOverlay}>
          <div className={styles.customEventModal}>
            <h4 className={styles.customEventModalTitle}>{t('availability_custom_event_details', 'Custom Event Details')}</h4>
            {!editingCustomEvent ? (
              <>
                <p><strong>{t('localization_name', 'Name')}:</strong> {activeCustomEvent.title}</p>
                <p><strong>{t('reports_date', 'Date')}:</strong> {toKualaLumpurISODateStrict(activeCustomEvent.event_date)}</p>
                <p><strong>{t('tasks_label_description', 'Description')}:</strong> {activeCustomEvent.description || '-'}</p>
                <p><strong>{t('availability_created_by', 'Created by')}:</strong> {activeCustomEvent.created_by_username || '-'}</p>
                <div className={styles.customEventModalActions}>
                  {canManageCustomEvents && (
                    <button type="button" className={styles.saveBtn} onClick={() => setEditingCustomEvent(true)}>
                      {t('teams_edit', 'Edit')}
                    </button>
                  )}
                  {canManageCustomEvents && (
                    <button type="button" className={styles.deleteDangerBtn} onClick={handleCustomEventDelete} disabled={submittingCustom}>
                      {t('teams_delete', 'Delete')}
                    </button>
                  )}
                  <button type="button" className={styles.cancelBtn} onClick={closeCustomEventDialog}>
                    {t('availability_close', 'Close')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.customEventModalForm}>
                  <input
                    type="text"
                    className={styles.customEventInput}
                    value={customEditTitle}
                    onChange={(e) => setCustomEditTitle(e.target.value)}
                    placeholder={t('localization_name', 'Name')}
                  />
                  <input
                    type="date"
                    className={styles.customEventInput}
                    value={customEditDate}
                    onChange={(e) => setCustomEditDate(e.target.value)}
                  />
                  <textarea
                    className={styles.customEventInput}
                    value={customEditDescription}
                    onChange={(e) => setCustomEditDescription(e.target.value)}
                    placeholder={t('tasks_label_description', 'Description')}
                  />
                </div>
                <div className={styles.customEventModalActions}>
                  <button type="button" className={styles.saveBtn} onClick={handleCustomEventUpdate} disabled={submittingCustom}>
                    {t('common_save', 'Save')}
                  </button>
                  <button type="button" className={styles.cancelBtn} onClick={() => setEditingCustomEvent(false)} disabled={submittingCustom}>
                    {t('common_cancel', 'Cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </div>

      {!isAdmin && (
        <div className={styles.upcomingEventsCard}>
          <h4 className={styles.upcomingEventsTitle}>{t('availability_upcoming_admin_events', 'Upcoming Admin Events (Next 3 Months)')}</h4>
          {upcomingAdminEvents.length === 0 ? (
            <p className={styles.upcomingEventsEmpty}>{t('availability_no_upcoming_admin_events', 'No upcoming admin events.')}</p>
          ) : (
            <>
              <div className={styles.upcomingEventsList}>
                {visibleUpcomingEvents.map((evt) => (
                  <div key={`upcoming-${evt.id}`} className={styles.upcomingEventCardItem}>
                    <div className={styles.upcomingEventDate}>{displayDateDDMMMYYYY(evt.eventDateISO)}</div>
                    <div className={styles.upcomingEventBody}>
                      <div className={styles.upcomingEventTitle}>{evt.title}</div>
                      <div className={styles.upcomingEventDescription}>{evt.description || '-'}</div>
                    </div>
                  </div>
                ))}
              </div>
              {upcomingAdminEvents.length > 5 && (
                <button
                  type="button"
                  className={styles.upcomingToggleBtn}
                  onClick={() => setShowAllUpcoming((prev) => !prev)}
                >
                  {showAllUpcoming ? t('availability_show_less', 'Show Less') : `${t('availability_show_more_prefix', 'Show More')} (${upcomingAdminEvents.length - 5} ${t('availability_more_suffix', 'more')})`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default AvailabilityCalendar;
